// Configuration API + helper avec support hors ligne
import { offlineManager } from './offlineManager';
import { secureStorage } from './secureStorage';
import { BACKEND_URL } from './backendUrl';
import { notifyDataChanged } from './dataChanges';

export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

console.log('🔧 Configuration API:', { BACKEND_URL, API });

// Rafraîchissement du jeton d'accès court via le jeton de rafraîchissement (déduplication des appels concurrents)
let _refreshPromise = null;
async function tryRefreshToken() {
  const rt = secureStorage.getRefreshToken();
  if (!rt) return null;
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const r = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!r.ok) return null;
      const d = await r.json();
      if (d && d.access_token) {
        secureStorage.setToken(d.access_token);
        if (d.refresh_token) secureStorage.setRefreshToken(d.refresh_token);
        return d.access_token;
      }
      return null;
    } catch (e) {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// API Helper avec support hors ligne amélioré - Version avec détection offline robuste
export const apiCall = async (endpoint, options = {}) => {
  const token = secureStorage.getToken();
  const method = options.method || 'GET';
  const fullEndpoint = `${API}${endpoint}`;

  // CORRECTION CRITIQUE : Vérifier le mode hors ligne via multiple méthodes
  const isOfflineMode = !navigator.onLine || window.isForceOffline || false;
  // L'authentification DOIT toujours passer par le serveur (jamais le mode hors ligne),
  // sinon la connexion échoue de façon intermittente quand navigator.onLine est faux à tort (fréquent sur mobile).
  const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/init');

  try {
    console.log(`🌐 DEBUG - Requête: ${method} ${fullEndpoint} (Online: ${navigator.onLine}, ForceOffline: ${window.isForceOffline})`);

    if (!isOfflineMode || isAuthEndpoint) {
      // MODE ONLINE : Utiliser fetch natif
      const fetchConfig = {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
      };

      if (options.body) {
        fetchConfig.body = options.body;
      } else if (options.data) {
        fetchConfig.body = JSON.stringify(options.data);
      }

      const response = await fetch(fullEndpoint, fetchConfig);

      // Jeton d'accès court expiré : rafraîchir automatiquement puis réessayer une fois (hors endpoints d'auth)
      let finalResponse = response;
      if (response.status === 401 && !isAuthEndpoint && token) {
        const newToken = await tryRefreshToken();
        if (newToken) {
          fetchConfig.headers = { ...fetchConfig.headers, Authorization: `Bearer ${newToken}` };
          finalResponse = await fetch(fullEndpoint, fetchConfig);
        }
      }

      if (!finalResponse.ok) {
        let detail = null;
        try {
          const errBody = await finalResponse.json();
          detail = errBody?.detail;
        } catch (e) {}
        const err = new Error(detail || `HTTP ${finalResponse.status}: ${finalResponse.statusText}`);
        err.status = finalResponse.status;
        err.detail = detail;
        throw err;
      }

      const data = await finalResponse.json();
      console.log(`✅ DEBUG ONLINE - Données reçues: ${Array.isArray(data) ? data.length : 'objet'} éléments`);

      // CORRECTION CRITIQUE : Sauvegarder les données récupérées pour usage hors ligne
      if (method === 'GET' && data) {
        try {
          await offlineManager.saveDataForOfflineUse(fullEndpoint, data);
          console.log(`💾 DEBUG ONLINE - Données sauvegardées pour usage hors ligne`);
        } catch (error) {
          console.warn('⚠️ Erreur sauvegarde cache hors ligne:', error);
        }
      }

      if (method !== 'GET' && !isAuthEndpoint) {
        notifyDataChanged({ endpoint, method, source: 'online' });
      }

      return data;
    } else {
      // MODE HORS LIGNE : Utiliser offlineManager
      console.log(`🔌 DEBUG HORS LIGNE - Utilisation offlineManager pour ${method}`);

      if (method === 'GET') {
        const result = await offlineManager.getData(fullEndpoint);
        console.log(`💾 DEBUG HORS LIGNE GET - Récupéré: ${Array.isArray(result) ? result.length : 'objet'} éléments`);
        return result;
      } else if (method === 'POST') {
        const data = options.data || (options.body ? JSON.parse(options.body) : {});
        const result = await offlineManager.postData(fullEndpoint, data);
        console.log(`💾 DEBUG HORS LIGNE POST - Création sauvegardée:`, result);
        notifyDataChanged({ endpoint, method, source: 'offline' });
        return result;
      } else if (method === 'PUT') {
        const data = options.data || (options.body ? JSON.parse(options.body) : {});
        const result = await offlineManager.putData(fullEndpoint, data);
        console.log(`💾 DEBUG HORS LIGNE PUT - Modification sauvegardée:`, result);
        notifyDataChanged({ endpoint, method, source: 'offline' });
        return result;
      } else if (method === 'DELETE') {
        const id = endpoint.split('/').pop();
        const result = await offlineManager.deleteData(fullEndpoint, id);
        console.log(`💾 DEBUG HORS LIGNE DELETE - Suppression sauvegardée:`, result);
        notifyDataChanged({ endpoint, method, source: 'offline' });
        return result;
      }
    }
  } catch (error) {
    console.error(`❌ DEBUG - Erreur API ${method} ${fullEndpoint}:`, error);

    // FALLBACK HORS LIGNE : Si fetch échoue, utiliser offlineManager
    // (JAMAIS pour l'authentification : on remonte l'erreur réseau pour afficher un message clair)
    if (!isAuthEndpoint && (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError'))) {
      console.log(`🔌 FALLBACK HORS LIGNE automatique pour ${method} ${fullEndpoint}`);

      try {
        if (method === 'GET') {
          return await offlineManager.getData(fullEndpoint);
        } else if (method === 'POST') {
          const data = options.data || (options.body ? JSON.parse(options.body) : {});
          const result = await offlineManager.postData(fullEndpoint, data);
          console.log(`💾 FALLBACK POST - Création sauvegardée hors ligne:`, result);
          notifyDataChanged({ endpoint, method, source: 'offline-fallback' });
          return result;
        } else if (method === 'PUT') {
          const data = options.data || (options.body ? JSON.parse(options.body) : {});
          const result = await offlineManager.putData(fullEndpoint, data);
          notifyDataChanged({ endpoint, method, source: 'offline-fallback' });
          return result;
        } else if (method === 'DELETE') {
          const id = endpoint.split('/').pop();
          const result = await offlineManager.deleteData(fullEndpoint, id);
          notifyDataChanged({ endpoint, method, source: 'offline-fallback' });
          return result;
        }
      } catch (offlineError) {
        console.error(`❌ Erreur offlineManager ${method}:`, offlineError);
        throw new Error(`Impossible de traiter la requête hors ligne: ${offlineError.message}`);
      }
    }

    // Rediriger vers /login uniquement pour une session authentifiée expirée,
    // PAS pour l'endpoint de login lui-même (sinon le message d'erreur ne s'affiche pas)
    if (
      (error.status === 401 || error.message?.includes('401')) &&
      !endpoint.startsWith('/auth/login') &&
      secureStorage.getToken()
    ) {
      secureStorage.clearAuth();
      window.location.href = '/login';
    }
    throw error;
  }
};
